import { JSONValue } from 'ai'

/**
 * consume-stream
 *
 * Primary responsibilities:
 * - Consume the HTTP streaming response body
 * - Parse ai@2.2.37 stream parts and return { text, data } for each line
 *
 * Dependencies:
 * - ai JSONValue
 *
 * Notes:
 * - ai@2.2.37 recognizes codes 0–8 (see StreamStringPrefixes).
 * - This app also accepts code 9 for live reasoning summaries.
 * - Inline tool markers must be carried via ai-supported "data" parts (code 2),
 *   using a payload shape like: { __inline_tool_marker: { kind, event } }.
 */

type StreamPartCode = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9'

type StreamToolEvent =
  | {
      type: 'tool.start'
      toolId: string
      toolName: string
      sequence?: number
      request?: unknown
      displayTitle?: string
    }
  | {
      type: 'tool.progress'
      toolId: string
      toolName: string
      sequence?: number
      request?: unknown
      displayTitle?: string
      progress?: { message?: string; percent?: number; step?: string }
    }
  | {
      type: 'tool.complete'
      toolId: string
      toolName: string
      sequence?: number
      result?: unknown
    }
  | {
      type: 'tool.error'
      toolId: string
      toolName: string
      sequence?: number
      error?: string
    }

export type ParsedStreamToolMarker = {
  kind: 'tool_marker'
  event: StreamToolEvent
}

function createAbortError() {
  const error = new Error('The operation was aborted.')
  error.name = 'AbortError'
  return error
}

export async function consumeReadableStream(
  stream: ReadableStream<Uint8Array>,
  callback: (chunk: string) => void,
  signal: AbortSignal
): Promise<void> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let aborted = signal.aborted

  const handleAbort = () => {
    aborted = true
    void reader.cancel().catch(() => {})
  }

  try {
    signal.addEventListener('abort', handleAbort, { once: true })

    while (true) {
      if (aborted || signal.aborted) {
        throw createAbortError()
      }

      const { done, value } = await reader.read()

      if (done) {
        if (aborted || signal.aborted) {
          throw createAbortError()
        }

        const tail = decoder.decode()
        if (tail) {
          callback(tail)
        }
        break
      }

      if (value) {
        if (aborted || signal.aborted) {
          throw createAbortError()
        }

        callback(decoder.decode(value, { stream: true }))
      }
    }
  } catch (error) {
    if (signal.aborted) {
      throw (error instanceof Error ? error : createAbortError())
    }

    throw error
  } finally {
    signal.removeEventListener('abort', handleAbort)
    reader.releaseLock()
  }
}

type StreamPartParser = {
  parse: (jsonValue: JSONValue) => { text: string; data: any }
}

export function parseDataStream(line: string): { text: string; data: any } {
  // ai@2.2.37 format: "<code>:<json>\n" (no required whitespace)
  // Some upstreams may emit "<code>: <json>", so trim a single leading space.

  const firstSeparatorIndex = line.indexOf(':')

  if (firstSeparatorIndex === -1) {
    throw new Error('Failed to parse stream string. No separator found.')
  }

  const prefix = line.slice(0, firstSeparatorIndex) as StreamPartCode
  const textValue = line.slice(firstSeparatorIndex + 1).replace(/^\s/, '')
  const jsonValue: JSONValue = JSON.parse(textValue)

  const streamPartsByCode: Record<StreamPartCode, StreamPartParser> = {
    // 0: text
    '0': {
      parse: (v) => ({ text: v as string, data: null })
    },

    // 1: function_call
    '1': { parse: (v) => ({ text: '', data: v as any }) },

    // 2: data
    //
    // Inline tool markers are carried via this stream part using:
    //   { __inline_tool_marker: { kind: 'tool_marker', event: ... } }
    '2': { parse: (v) => ({ text: '', data: v as any }) },

    // 3: error
    '3': { parse: (v) => ({ text: '', data: v as any }) },

    // 4: assistant_message
    '4': { parse: (v) => ({ text: '', data: v as any }) },

    // 5: assistant_control_data
    '5': { parse: (v) => ({ text: '', data: v as any }) },

    // 6: data_message
    '6': { parse: (v) => ({ text: '', data: v as any }) },

    // 7: tool_calls payload (ToolCallPayload shape)
    '7': {
      parse: (v) => ({ text: '', data: v as any })
    },

    // 8: message_annotations payload (array)
    '8': {
      parse: (v) => ({ text: '', data: v as any })
    },

    // 9: reasoning summary stream payload
    '9': {
      parse: (v) => {
        const reasoningData = v as { type?: unknown; text?: unknown }
        if (
          reasoningData.type === 'reasoning_summary' &&
          typeof reasoningData.text === 'string'
        ) {
          return {
            text: '',
            data: {
              reasoning_summary_stream: {
                type: 'reasoning_summary',
                text: reasoningData.text
              }
            }
          }
        }

        return { text: '', data: reasoningData }
      }
    }
  }

  const part = streamPartsByCode[prefix]
  if (!part) {
    throw new Error(`Failed to parse stream string. Invalid code ${prefix}.`)
  }

  return part.parse(jsonValue)
}
